/** Светлая и тёмная терракотовые темы. До 25.10.2026 темы были красные и
 *  назывались `light-red` / `dark-red` — эти значения ещё лежат в localStorage
 *  у пользователей, их принимает `features/theme/model/slice.ts`. */
export type ThemeName = 'light' | 'dark';

/** Терракотовая палитра бренда (ребрендинг 25.10.2026) — от светлого льна до
 *  тёмной умбры. Из неё собраны темы ниже; в страницах — для статичных цветов
 *  (серии графиков), всё остальное брать из темы. */
export const terracotta = {
  linen: '#D6CABB',
  camel: '#C29E7E',
  clay: '#B26A4A',
  rust: '#903917',
  umber: '#703119',
} as const;

export interface OsnovaTheme {
  name: ThemeName;
  colors: {
    primary: string;
    primaryHover: string;
    primarySoft: string;
    background: string;
    backgroundElevated: string;
    surface: string;
    surfaceHover: string;
    border: string;
    borderStrong: string;
    text: { primary: string; secondary: string; muted: string };
    accent: string;
    status: {
      success: string;
      warning: string;
      danger: string;
      info: string;
    };
    gradient: {
      brand: string;
      surface: string;
    };
    /** Каналы «r, g, b» фирменного цвета для полупрозрачных тонировок
     *  `rgba(${tint.base}, 0.12)`: `base` — заливки, `glow` — кромки и свечение
     *  (стеклянные баннеры, эмблемы, тени активных кнопок). */
    tint: {
      base: string;
      glow: string;
    };
    /** Полосы прокрутки — единые во всей ОСНОВЕ (GlobalStyle). Ползунок
     *  `thumb` — терракота бренда, при наведении и перетаскивании —
     *  `thumbHover`; дорожка `track` — нейтральная, видна всегда. */
    scrollbar: {
      track: string;
      thumb: string;
      thumbHover: string;
    };
  };
  spacing: (n: number) => string;
  radius: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    pill: string;
  };
  shadow: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    focus: string;
    primary: string;
  };
  font: { family: string; mono: string };
  motion: {
    fast: string;
    base: string;
    slow: string;
    spring: string;
    easeOut: string;
    easeInOut: string;
  };
  z: {
    base: number;
    sticky: number;
    overlay: number;
    modal: number;
    toast: number;
  };
}

const motion = {
  fast: '120ms',
  base: '200ms',
  slow: '360ms',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeInOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
};

const radius = {
  xs: '4px',
  sm: '6px',
  md: '10px',
  lg: '14px',
  xl: '20px',
  pill: '999px',
};

const spacing = (n: number) => `${n * 4}px`;

const z = { base: 1, sticky: 100, overlay: 900, modal: 1000, toast: 1100 };

const font = {
  family: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', 'SF Mono', Consolas, monospace",
};

/* Контраст (WCAG) проверен на фонах обеих тем: основной текст ≥ 14:1,
   вторичный ≥ 5,7:1, фирменный цвет текстом ≥ 4,5:1, белый на кнопке —
   7,5:1 в светлой и 3,7:1 в тёмной (у прежнего красного было 5,9 и 3,2).
   «Ошибка» (`danger`) осталась красной: от терракоты она отличается втрое
   сильнее, чем от прежнего красного бренда (ΔE 27 против 10 в светлой, 32
   против 13 в тёмной), при дальтонизме — ΔE ≥ 6,8. */

export const lightTheme: OsnovaTheme = {
  name: 'light',
  colors: {
    primary: terracotta.rust,
    primaryHover: terracotta.umber,
    primarySoft: 'rgba(144, 57, 23, 0.08)',
    background: '#F7F3EE',
    backgroundElevated: '#FFFDFA',
    surface: '#F1EBE3',
    surfaceHover: '#E8E0D5',
    border: '#E2D8CB',
    borderStrong: terracotta.linen,
    text: { primary: '#2A1C15', secondary: '#68584C', muted: '#9A887A' },
    accent: '#2A1C15',
    status: {
      success: '#1E8E3E',
      warning: '#F57C00',
      danger: '#C62828',
      info: '#1976D2',
    },
    gradient: {
      brand: `linear-gradient(135deg, ${terracotta.clay} 0%, ${terracotta.umber} 100%)`,
      surface: 'linear-gradient(180deg, #FFFDFA 0%, #F7F3EE 100%)',
    },
    tint: {
      base: '144, 57, 23',
      glow: '178, 106, 74',
    },
    scrollbar: {
      track: 'rgba(42, 28, 21, 0.07)',
      thumb: terracotta.clay,
      thumbHover: terracotta.rust,
    },
  },
  spacing,
  radius,
  shadow: {
    xs: '0 1px 1px rgba(42, 28, 21, 0.05)',
    sm: '0 1px 3px rgba(42, 28, 21, 0.07), 0 1px 2px rgba(42, 28, 21, 0.05)',
    md: '0 4px 12px rgba(42, 28, 21, 0.09), 0 2px 4px rgba(42, 28, 21, 0.05)',
    lg: '0 12px 32px rgba(42, 28, 21, 0.13), 0 4px 8px rgba(42, 28, 21, 0.07)',
    focus: '0 0 0 3px rgba(144, 57, 23, 0.24)',
    primary: '0 6px 20px rgba(144, 57, 23, 0.28)',
  },
  font,
  motion,
  z,
};

export const darkTheme: OsnovaTheme = {
  name: 'dark',
  colors: {
    // Глина палитры, чуть светлее (#B26A4A → #BD7350): на тёмных карточках
    // фирменный цвет текстом иначе не добирал контраста 4,5:1.
    primary: '#BD7350',
    primaryHover: '#C98463',
    primarySoft: 'rgba(189, 115, 80, 0.14)',
    // Фоны, рамки и текст — нейтральные тёмно-серые, терракота только в
    // акцентах. С 25.10.2026 фоны были тёмно-коричневые, от них за день
    // уставали глаза (решение юзера 26.10.2026). Светлота — как у тёмной темы
    // до ребрендинга (#121214 / #1A1A1D / #1F1F23), только без синевы.
    background: '#131313',
    backgroundElevated: '#1A1A1A',
    surface: '#1E1E1E',
    surfaceHover: '#262626',
    border: '#2E2E2E',
    borderStrong: '#3D3D3D',
    text: { primary: '#F2F2F2', secondary: '#B0B0B0', muted: '#7C7C7C' },
    accent: '#FFFFFF',
    status: {
      success: '#69D483',
      warning: '#FFB74D',
      danger: '#FF6B6B',
      info: '#64B5F6',
    },
    gradient: {
      brand: `linear-gradient(135deg, #BD7350 0%, ${terracotta.rust} 100%)`,
      surface: 'linear-gradient(180deg, #1A1A1A 0%, #131313 100%)',
    },
    tint: {
      base: '178, 106, 74',
      glow: '201, 132, 99',
    },
    scrollbar: {
      track: 'rgba(255, 255, 255, 0.07)',
      thumb: terracotta.clay,
      thumbHover: '#C98463',
    },
  },
  spacing,
  radius,
  shadow: {
    xs: '0 1px 1px rgba(0, 0, 0, 0.4)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.3)',
    md: '0 4px 12px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4)',
    lg: '0 12px 32px rgba(0, 0, 0, 0.75), 0 4px 8px rgba(0, 0, 0, 0.5)',
    focus: '0 0 0 3px rgba(189, 115, 80, 0.36)',
    primary: '0 6px 20px rgba(189, 115, 80, 0.32)',
  },
  font,
  motion,
  z,
};

export const themesByName: Record<ThemeName, OsnovaTheme> = {
  light: lightTheme,
  dark: darkTheme,
};
